import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { CustomerDto, CustomerEditDto } from 'src/store/customers/customer';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum CustomerTypes {
  FetchAll = 'customers/FetchAll',
  Add = 'customers/Add',
  Update = 'customers/Update',
  Delete = 'customers/Delete',
  Failure = 'customers/Failure'
}

export type CustomerAction =
  | { type: CustomerTypes.FetchAll; customers: CustomerDto[] }
  | { type: CustomerTypes.Add; customer: CustomerDto }
  | { type: CustomerTypes.Update; customer: CustomerDto }
  | { type: CustomerTypes.Delete; customer: CustomerDto }
  | { type: CustomerTypes.Failure; errors: any };

export const actionCreators = {
  fetchCustomers: (customers: CustomerDto[]): CustomerAction => ({
    type: CustomerTypes.FetchAll,
    customers
  }),
  addCustomer: (customer: CustomerDto): CustomerAction => ({
    type: CustomerTypes.Add,
    customer
  }),
  updateCustomer: (customer: CustomerDto): CustomerAction => ({
    type: CustomerTypes.Update,
    customer
  }),
  deleteCustomer: (customer: CustomerDto): CustomerAction => ({
    type: CustomerTypes.Delete,
    customer
  }),
  customerRequestFailure: (errors: any): CustomerAction => ({
    type: CustomerTypes.Failure,
    errors
  })
};

export const fetchCustomers = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(CustomerTypes.FetchAll));
    const { ok, data, error } = await getRequest<CustomerDto[]>('/customers');
    dispatch(requestEnd(CustomerTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchCustomers(data))
      : dispatch(actionCreators.customerRequestFailure(error));
  };
};

export const createCustomer = (customer: CustomerEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      '/customers',
      customer
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('customer.created')));
      return dispatch(actionCreators.addCustomer({ ...customer, id: data }));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.customerRequestFailure(error));
  };
};

export const editCustomer = (id: number, customer: CustomerEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/customers/${id}`, customer);

    if (ok) {
      dispatch(alertSuccess(i18n.t('customer.updated')));
      return dispatch(actionCreators.updateCustomer(customer as CustomerDto));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.customerRequestFailure(error));
  };
};

export const deleteCustomer = (id: number, customer: CustomerDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/customers/${id}`, customer);

    if (ok) {
      dispatch(alertSuccess(i18n.t('customer.deleted')));
      return dispatch(actionCreators.deleteCustomer(customer));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.customerRequestFailure(error));
  };
};
